import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Modal, ModalBody, ModalHeader, Button } from "react-bootstrap";
import EditIcon from "@mui/icons-material/Edit";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import { AddIcon } from "@components/icons";

import {
  colorsPulse,
  colorsDendrometer,
  colorsSoilMoisture,
  colorsSoilWaterTension,
  GREY,
  GREEN,
  YELLOW,
  RED,
  shadeColor,
  findClosest,
} from "@helper/colors";
import MoistureGauge from "./MoistureGauge";
import HealthThresholdSlider from "./Form/AddForm/steps/HTs";
import SoilThresholdSlider from "./Form/AddForm/steps/SoilThresholds";
import { statusList } from "@helper/healthState";
import { useBackgroundTreeSpecies } from "@hooks/useBackgroundData";
export function getBrowserName():
  | "Chrome"
  | "Edge"
  | "Opera"
  | "Firefox"
  | "Internet Explorer"
  | "Arc"
  | "Unknown" {
  const ua = navigator.userAgent;

  if (/Edg\//.test(ua)) {
    return "Edge";
  } else if (/OPR\//.test(ua)) {
    return "Opera";
  } else if (/Firefox\//.test(ua)) {
    return "Firefox";
  } else if (/MSIE |Trident\//.test(ua)) {
    return "Internet Explorer";
  } else if (/Chrome\//.test(ua)) {
    if (/Arc\//.test(ua)) {
      return "Arc";
    }
    if (!/Edg\//.test(ua) && !/OPR\//.test(ua)) {
      return "Chrome";
    }
  }

  return "Unknown";
}

function getSensorType(sensor) {
  const sensorType = sensor.pulse_sensor
    ? "pulse"
    : sensor.soil_moisture_sensor
    ? "soil_moisture"
    : sensor.soil_water_tension_sensor
    ? "soil_water_tension"
    : "dendrometer";
  return sensorType;
}
function statePulse(tree, sensor) {
  const moistureContent = sensor?.pulse_sensor?.moisture_content ?? 0;
  const frost = sensor?.pulse_sensor?.frost ?? false;
  if (sensor.sensor_state != 0) {
    return 3;
  }
  if (frost) {
    return 4;
  }
  if (moistureContent > tree.blue_threshold) {
    return 5;
  }
  if (moistureContent > tree.green_threshold) {
    return 0;
  }
  if (moistureContent > tree.yellow_threshold) {
    return 1;
  }
  return 2;
}
function stateSoilMoisture(tree, sensor) {
  const soil = tree.soil;
  const yellow_threshold = soil ? soil.pwp : 10;
  const green_threshold = soil
    ? soil.pwp + ((soil.max_nf / 2) * (soil.max_nf - soil.pwp)) / soil.max_nf
    : 25;
  const blue_threshold = soil ? soil.max_nf : 45;
  const value = sensor?.soil_moisture_sensor?.soil_moisture | 0;
  if (sensor.sensor_state != 0) {
    return 3;
  }
  if (value > blue_threshold) {
    return 5;
  }
  if (value > green_threshold) {
    return 0;
  }
  if (value > yellow_threshold) {
    return 1;
  }
  return 2;
}

function stateSoilWaterTension(tree, sensor) {
  const soil = tree.soil;
  const yellow_threshold = soil
    ? soil.yellow_threshold_swt
      ? -Math.pow(10, soil.yellow_threshold_swt) / 10
      : -120
    : -120;
  const green_threshold = soil
    ? soil.green_threshold_swt
      ? -Math.pow(10, soil.green_threshold_swt) / 10
      : -60
    : -60;
  const blue_threshold = soil
    ? soil.blue_threshold_swt
      ? -Math.pow(10, soil.blue_threshold_swt) / 10
      : -6
    : -6;
  const value = sensor?.soil_water_tension_sensor?.soil_water_tension | 0;
  if (sensor.sensor_state != 0) {
    return 3;
  }
  if (value > blue_threshold) {
    return 5;
  }
  if (value > green_threshold) {
    return 0;
  }
  if (value > yellow_threshold) {
    return 1;
  }
  return 2;
}

function stateDendrometer(sensor) {
  if (sensor.sensor_state != 0) {
    return 3;
  }
  const droughtDays = Number(sensor?.dendrometer_sensor?.drought_days);
  if (!Number.isFinite(droughtDays)) {
    return 3;
  }
  const normalizedDays = Math.max(0, Math.min(14, droughtDays)) / 14;
  const [, color] = findClosest(normalizedDays, colorsDendrometer());
  if (color === GREEN) {
    return 0;
  }
  if (color === YELLOW) {
    return 1;
  }
  if (color === RED) {
    return 2;
  }
  return 3;
}

function treeStateDescription(t, sensor, tree) {
  let state = 3; // unknown
  const sensorType = getSensorType(sensor);
  if (sensorType == "pulse") {
    state = statePulse(tree, sensor);
  } else if (sensorType == "soil_moisture") {
    state = stateSoilMoisture(tree, sensor);
  } else if (sensorType == "soil_water_tension") {
    state = stateSoilWaterTension(tree, sensor);
  } else if (sensorType == "dendrometer") {
    state = stateDendrometer(sensor);
  }
  const desc = `${t(statusList[state].label)}: ${t(statusList[state].todo)}`;
  return desc;
}
function sensorLabel({ sensorIconInfo }) {
  const averageCharWidth = 8;
  const estimatedWidth = sensorIconInfo.name ? sensorIconInfo.name.length * averageCharWidth : 0;
  const maxRight = 250;

  let x = sensorIconInfo.x - estimatedWidth / 2;
  x = Math.max(0, Math.min(x, maxRight - estimatedWidth));

  return (
    <div
      style={{
        position: "absolute",
        left: x,
        top: sensorIconInfo.y - 27,
        zIndex: 10,
        pointerEvents: "none",
        background: "white",
        padding: "2px 6px",
        borderRadius: "10px",
        border: "1px solid #ccc",
        boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
        fontSize: "12px",
        whiteSpace: "nowrap",
      }}
    >
      {sensorIconInfo.name}
    </div>
  );
}

function getByPath(obj: any, path: string): any {
  return path.split(".").reduce((acc, part) => acc?.[part], obj);
}
function findMinByNestedKey<T>(arr: T[], path: string): T | undefined {
  if (arr.length === 0) return undefined;
  return arr.reduce((min, current) => {
    const minVal = getByPath(min, path);
    const currVal = getByPath(current, path);
    console.log(minVal);
    return currVal < minVal ? current : min;
  });
}
const Tree = (args) => {
  const { t } = useTranslation();
  const tree = args.tree;
  const edit = args.edit;
  const setEdit = args.setEdit;
  const soilEdit = args.soilEdit;
  const setSoilEdit = args.setSoilEdit;
  const selectedInstallation = args.selectedInstallation;
  const setSelectedInstallation = args.setSelectedInstallation;
  const showData = args.showData;
  const setShowData = args.setShowData;
  const onAddSensor = args.onAddSensor;
  const previewHeight = args.previewHeight;
  const previewDepth = args.previewDepth;
  const previewSensorType = args.previewSensorType;
  const onPreviewHeightChange = args.onPreviewHeightChange;
  const onPreviewDepthChange = args.onPreviewDepthChange;
  const onPreviewInstall = args.onPreviewInstall;
  const hideInstallations = args.hideInstallations;
  const hideEditButtons = args.hideEditButtons === true;
  const showSelectedSensorPanel = args.showSelectedSensorPanel !== false;
  const centerTree = args.centerTree === true;
  const allowPreviewDrag = args.allowPreviewDrag === true;
  const onOpenConfigPanel = args.onOpenConfigPanel;
  const [soilWaterTensionType, setSoilWaterTensionType] = useState("kPa");
  const { treeSpecies } = useBackgroundTreeSpecies();
  const calibrationSpeciesName = React.useMemo(() => {
    const speciesId = tree?.species_id ?? tree?.species?.id;
    if (!speciesId) return null;
    const speciesList = Array.isArray(treeSpecies) ? treeSpecies : [];
    const entry = speciesList.find((sp) => sp.id === speciesId);
    const calibId = entry?.calibration_species;
    if (!calibId) return null;
    return calibId
      .split("_")
      .map((part: string, i: number) =>
        i === 0 ? part.charAt(0).toUpperCase() + part.slice(1) : part
      )
      .join(" ");
  }, [tree?.species_id, tree?.species?.id, treeSpecies]);
  const installations = tree?.installations
    ? tree.installations.filter((installation) => !installation.removed_at)
    : [];
  const validLabelBrowser = ["Firefox"].includes(getBrowserName());
  const previousTreeIdRef = useRef(tree?.id);
  useEffect(() => {
    if (previousTreeIdRef.current !== tree?.id) {
      setSelectedInstallation(null);
      previousTreeIdRef.current = tree?.id;
    }
  }, [tree?.id, setSelectedInstallation]);

  // SOIL
  function getDepthColor(depth) {
    let soilColor = GREY;
    const soilInstallationsDepth = installations.filter(
      (installation) =>
        installation.sensor &&
        (installation.sensor.soil_moisture_sensor !== null ||
          installation.sensor.soil_water_tension_sensor !== null) &&
        installation.depth == depth
    );
    if (soilInstallationsDepth.length == 0) {
      return GREY;
    }
    const soilInstallationsDepthSWT = soilInstallationsDepth.filter(
      (installation) => installation.sensor.soil_water_tension_sensor !== null
    );
    const soilInstallationsDepthSM = soilInstallationsDepth.filter(
      (installation) => installation.sensor.soil_moisture_sensor !== null
    );
    let sensor = soilInstallationsDepth[0].sensor;
    if (soilInstallationsDepthSWT.length > 0) {
      const installation: any = findMinByNestedKey(
        soilInstallationsDepthSWT,
        "sensor.soil_water_tension_sensor.soil_water_tension"
      );
      sensor = installation.sensor;
    } else if (soilInstallationsDepthSM.length > 0) {
      const installation: any = findMinByNestedKey(
        soilInstallationsDepthSM,
        "sensor.soil_moisture_sensor.soil_moisure"
      );
      sensor = installation.sensor;
    }
    let soilColors = colorsSoilWaterTension(tree.soil);
    let soilState = "UN";
    let value = 0;
    if (sensor.soil_moisture_sensor !== null) {
      soilColors = colorsSoilMoisture(
        tree.soil ? tree.soil.pwp : 10,
        tree.soil ? tree.soil.max_nf : 25,
        tree.soil ? tree.soil.max_f : 45
      );
      value = sensor.soil_moisture_sensor.soil_moisture / 100;
      soilState = sensor.soil_moisture_sensor.soil_state;
    } else {
      value = sensor.soil_water_tension_sensor.soil_water_tension;
      soilState = sensor.soil_water_tension_sensor.soil_state;
    }
    if (soilState != "UN") {
      const closestPair = findClosest(value, soilColors);
      soilColor = closestPair[1];
    }
    return soilColor;
  }
  const crownInstallations = installations.filter(
    (installation) => installation.sensor && installation.sensor.pulse_sensor !== null // installation.removed_at < new Date()
  );
  const dendrometerInstallations = installations.filter(
    (installation) => installation.sensor && installation.sensor.dendrometer_sensor !== null
  );
  const soilInstallations = installations.filter(
    (installation) =>
      installation.sensor &&
      (installation.sensor.soil_moisture_sensor !== null ||
        installation.sensor.soil_water_tension_sensor !== null)
  );
  const hasBall = (items) => {
    return items.some((item) => item.depth === 0);
  };

  const soilHasBall = hasBall(soilInstallations);
  const depths = soilInstallations.map((installation) => installation.depth);
  const depthsSet = new Set(depths);
  let soilColorBall = GREY;
  let soilColor30 = GREY;
  let soilColor60 = GREY;
  let soilColor90 = GREY;
  let soilColor120 = GREY;
  if (depthsSet.size == 1) {
    const sensor = soilInstallations[0].sensor;
    let soilColors30 = colorsSoilWaterTension(tree.soil);
    let soilState = "UN";
    let value = 0;
    if (sensor.soil_moisture_sensor !== null) {
      soilColors30 = colorsSoilMoisture(
        tree.soil ? tree.soil.pwp : 10,
        tree.soil ? tree.soil.max_nf : 25,
        tree.soil ? tree.soil.max_f : 45
      );
      value = sensor.soil_moisture_sensor.soil_moisture / 100;
      soilState = sensor.soil_moisture_sensor.soil_state;
    } else {
      value = sensor.soil_water_tension_sensor.soil_water_tension;
      soilState = sensor.soil_water_tension_sensor.soil_state;
    }
    if (soilState != "UN") {
      const closestPair = findClosest(value, soilColors30);
      soilColor30 = closestPair[1];
    }
  } else if (depthsSet.size > 1) {
    soilColor30 = getDepthColor(30);
    soilColor60 = getDepthColor(60);
    soilColor90 = getDepthColor(90);
  }
  if (depthsSet.size > 3) {
    soilColor120 = getDepthColor(120);
  }
  if (soilHasBall) {
    soilColorBall = getDepthColor(0);
  }

  const treeIconHeight = ["soil_moisture", "soil_water_tension"].includes(previewSensorType)
    ? 330
    : depthsSet.size > 1
    ? depthsSet.size > 3
      ? 330
      : 310
    : 250;
  const handleSensorClick = (installation) => {
    if (selectedInstallation?.id === installation?.id) {
      setSelectedInstallation(null);
      return;
    }
    setSelectedInstallation(installation);
  };
  // function calculateSensorYPosition(percentage) {
  function calculateSensorYPosition(percentage) {
    const trunkStartY = 0; // Top Y of the trunk
    const trunkHeight = 230; // Height of the trunk
    return trunkStartY + trunkHeight * ((100 - percentage) / 100); // Adjusting for 50% height
  }

  function calculateSensorXPosition(index) {
    const trunkStartX = 116; // Starting X of the trunk
    const trunkWidth = 0;
    const position =
      trunkStartX + trunkWidth / 2 + Math.floor((index + 1) / 2) * 45 * (-1) ** (index % 2); // If you have the trunk width, adjust accordingly
    return position;
  }
  const [draggingPreview, setDraggingPreview] = useState(false);
  const dragMovedRef = useRef(false);
  const svgRef = useRef<SVGSVGElement>(null);
  const updatePreviewFromClientRef = useRef<((clientY: number) => void) | null>(null);
  const isPreviewSoilSensor = ["soil_moisture", "soil_water_tension"].includes(previewSensorType);
  const previewDepthValue = Number(previewDepth);
  const hasPreviewDepth = Number.isFinite(previewDepthValue);
  const normalizeSoilPreviewDepth = (value) => {
    const depth = Math.round(Number(value));
    return [0, 30, 60, 90, 120].includes(depth) ? depth : 30;
  };
  const soilDepthToPercent = (depth) => (depth === 0 ? -5 : (30 - depth) / 3);
  const normalizedPreviewDepth = hasPreviewDepth
    ? normalizeSoilPreviewDepth(previewDepthValue)
    : 30;
  const previewHeightValue = Number(previewHeight);
  const hasPreviewHeight = Number.isFinite(previewHeightValue);
  const treeHeightValue = Number(tree?.height) || previewHeightValue || 30;
  const fallbackCrownHeight = Math.max(1, Math.round(treeHeightValue * 0.6));
  const yellowThreshold = Math.round(Number(tree?.yellow_threshold ?? 50));
  const greenThreshold = Math.round(Number(tree?.green_threshold ?? 70));
  const blueThreshold = Math.round(Number(tree?.blue_threshold ?? 100));
  const previewTooltip = `Rot < ${yellowThreshold}% · Gelb ≥ ${yellowThreshold}% · Grün ≥ ${greenThreshold}%${
    blueThreshold < 100 ? ` · Blau ≥ ${blueThreshold}%` : ""
  }`;
  const activePreviewTooltip = isPreviewSoilSensor ? "Drag to set sensor depth" : previewTooltip;
  const previewHeightPercent =
    hasPreviewHeight && treeHeightValue
      ? Math.max(0, Math.min(100, (previewHeightValue / treeHeightValue) * 100))
      : null;
  const previewPercent = isPreviewSoilSensor
    ? soilDepthToPercent(normalizedPreviewDepth)
    : previewHeightPercent;
  const previewY = previewPercent != null ? calculateSensorYPosition(previewPercent) : null;
  const previewX = calculateSensorXPosition(0);
  const previewInteractive = Boolean(
    onPreviewInstall || onPreviewHeightChange || onPreviewDepthChange
  );
  const previewDraggable = Boolean(
    previewY != null &&
      allowPreviewDrag &&
      ((isPreviewSoilSensor && onPreviewDepthChange) ||
        (!isPreviewSoilSensor && onPreviewHeightChange))
  );
  const previewSensor =
    previewSensorType === "soil_moisture"
      ? {
          sensor_state: 3,
          soil_moisture_sensor: { soil_moisture: 0, soil_state: "UN" },
          soil_water_tension_sensor: null,
          pulse_sensor: null,
          dendrometer_sensor: null,
        }
      : previewSensorType === "soil_water_tension"
      ? {
          sensor_state: 3,
          soil_moisture_sensor: null,
          soil_water_tension_sensor: { soil_water_tension: 0, soil_state: "UN" },
          pulse_sensor: null,
          dendrometer_sensor: null,
        }
      : previewSensorType === "dendrometer"
      ? {
          sensor_state: 3,
          dendrometer_sensor: { drought_days: 0 },
          pulse_sensor: null,
          soil_moisture_sensor: null,
          soil_water_tension_sensor: null,
        }
      : {
          sensor_state: 3,
          pulse_sensor: { moisture_content: 0, frost: false },
          dendrometer_sensor: null,
          soil_moisture_sensor: null,
          soil_water_tension_sensor: null,
        };
  const previewInstallation =
    previewInteractive && previewY != null
      ? {
          id: "preview-sensor",
          sensor: previewSensor,
          depth: normalizedPreviewDepth,
          height: Math.max(0, previewHeightValue || 0),
        }
      : null;
  const previewPosition = previewY != null ? { x: previewX, y: previewY } : null;
  const handlePreviewStart = () => {
    if (!previewDraggable) return;
    dragMovedRef.current = false;
    setDraggingPreview(true);
  };

  const updatePreviewFromClient = (clientY) => {
    if (!svgRef.current) return;
    dragMovedRef.current = true;
    const rect = svgRef.current.getBoundingClientRect();
    if (!rect.height) return;
    const viewHeight = treeIconHeight + 6;
    const relativeY = ((clientY - rect.top) / rect.height) * viewHeight;

    if (isPreviewSoilSensor && onPreviewDepthChange) {
      const depthTargets = [0, 30, 60, 90, 120].map((depth) => ({
        depth,
        y: calculateSensorYPosition(soilDepthToPercent(depth)),
      }));
      const closest = depthTargets.reduce((best, current) =>
        Math.abs(current.y - relativeY) < Math.abs(best.y - relativeY) ? current : best
      );
      onPreviewDepthChange(closest.depth);
      return;
    }

    if (!onPreviewHeightChange) return;
    const trunkStartY = 0;
    const trunkHeight = 230;
    const clampedY = Math.min(trunkStartY + trunkHeight, Math.max(trunkStartY, relativeY));
    const percent = 100 - ((clampedY - trunkStartY) / trunkHeight) * 100;
    const nextHeight = Math.max(1, Math.round((percent / 100) * treeHeightValue));
    onPreviewHeightChange(nextHeight);
  };
  // Keep ref always pointing to the latest version so the drag listener
  // never needs to be torn down and re-added when callbacks change reference.
  updatePreviewFromClientRef.current = updatePreviewFromClient;

  useEffect(() => {
    if (!draggingPreview) return;
    const handleMove = (event) => {
      updatePreviewFromClientRef.current?.(event.clientY);
    };
    const handleTouchMove = (event) => {
      if (!event.touches?.length) return;
      event.preventDefault();
      updatePreviewFromClientRef.current?.(event.touches[0].clientY);
    };
    const handleUp = () => {
      if (!dragMovedRef.current && onPreviewInstall) {
        onPreviewInstall();
      }
      setDraggingPreview(false);
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleUp);
    };
  }, [draggingPreview]);
  const findSmallestMoistureContent = (installations) => {
    // Use `Infinity` as the initial smallest value so that any value will be smaller
    let smallestMoistureContent = Infinity;

    installations.forEach((installation) => {
      const { moisture_content } = installation.sensor.pulse_sensor;
      if (moisture_content < smallestMoistureContent) {
        smallestMoistureContent = moisture_content;
      }
    });

    return smallestMoistureContent;
  };

  let fillCrownColor = GREY; // unknown
  const hasPulse = crownInstallations.some(
    (installation) => [0, 1].includes(installation.sensor.sensor_state) && !installation.removed_at
  );
  if (hasPulse) {
    const moistureContent = findSmallestMoistureContent(crownInstallations);
    const colorsPulseSensor = colorsPulse(
      tree.blue_threshold,
      tree.green_threshold,
      tree.yellow_threshold
    );
    const closestPair = findClosest(moistureContent / 100, colorsPulseSensor);
    fillCrownColor = closestPair[1];
  } else if (dendrometerInstallations.length > 0) {
    const validDendro = dendrometerInstallations.filter((installation) => !installation.removed_at);
    const maxDroughtDays = validDendro.reduce((max, installation) => {
      const days = installation.sensor?.dendrometer_sensor?.drought_days ?? 0;
      return Math.max(max, days);
    }, 0);
    const normalizedDays = Math.max(0, Math.min(14, maxDroughtDays)) / 14;
    const closestPair = findClosest(normalizedDays, colorsDendrometer());
    fillCrownColor = closestPair[1];
  }
  const fillCrown2Color = shadeColor(fillCrownColor, -18);

  const SensorIcon = ({
    onSensorClick,
    installation,
    installations,
    overridePosition = undefined,
    tooltip = undefined,
    onDragStart = undefined,
    disableHover = false,
    cursorOverride = undefined,
  }) => {
    const sensor = installation?.sensor;
    if (!installation || !sensor) {
      return null;
    }
    let index = 0;
    const sensorType = getSensorType(sensor);
    if (sensorType == "pulse" || sensorType == "dendrometer") {
      const crownInstallationsSorted = installations
        .filter((item) => {
          const itemSensor = item?.sensor;
          if (!itemSensor || item?.removed_at) return false;
          return itemSensor.pulse_sensor !== null || itemSensor.dendrometer_sensor !== null;
        })
        .sort((left, right) => {
          const leftType = getSensorType(left.sensor);
          const rightType = getSensorType(right.sensor);
          if (leftType !== rightType) {
            return leftType.localeCompare(rightType);
          }
          return (left.sensor?.name || "").localeCompare(right.sensor?.name || "");
        });
      index = crownInstallationsSorted.findIndex((item) => item.id === installation.id);
    }
    if (["soil_moisture", "soil_water_tension"].includes(sensorType)) {
      const depth = installation.depth;
      const soilInstallationsDepth = installations.filter(
        (installation) =>
          installation.sensor &&
          (installation.sensor.soil_moisture_sensor !== null ||
            installation.sensor.soil_water_tension_sensor !== null) &&
          depth === installation.depth
      );
      const soilInstallationsDepthSorted = soilInstallationsDepth.sort((inst1, inst2) =>
        inst1.sensor.name.localeCompare(inst2.sensor.name)
      );
      index = soilInstallationsDepthSorted.indexOf(installation);
    }
    function getColor(sensorType, sensor) {
      let color = GREY;
      if (sensorType == "soil_moisture") {
        const soilState = sensor.soil_moisture_sensor.soil_state;
        const soilMoisture = sensor.soil_moisture_sensor.soil_moisture;
        const soilColors = colorsSoilMoisture(
          tree.soil ? tree.soil.pwp : 10,
          tree.soil ? tree.soil.max_nf : 25,
          tree.soil ? tree.soil.max_f : 45
        );
        if (soilState != "UN") {
          const closestPair = findClosest(soilMoisture / 100, soilColors);
          color = closestPair[1];
        }
      } else if (sensorType == "soil_water_tension") {
        const soilState = sensor.soil_water_tension_sensor.soil_state;
        const soilWaterTension = sensor.soil_water_tension_sensor.soil_water_tension;
        if (soilState != "UN") {
          const closestPair = findClosest(soilWaterTension, colorsSoilWaterTension(tree.soil));
          color = closestPair[1];
        }
      } else if (sensorType == "pulse") {
        if (![2, 3, 4].includes(sensor.sensor_state)) {
          // not unknown
          const moistureContent = sensor.pulse_sensor.moisture_content;
          const closestPair = findClosest(
            moistureContent / 100,
            colorsPulse(tree.blue_threshold, tree.green_threshold, tree.yellow_threshold)
          );
          color = closestPair[1];
        }
      } else if (sensorType == "dendrometer") {
        if (![2, 3, 4].includes(sensor.sensor_state)) {
          const droughtDays = sensor?.dendrometer_sensor?.drought_days ?? 0;
          const normalizedDays = Math.max(0, Math.min(14, droughtDays)) / 14;
          const closestPair = findClosest(normalizedDays, colorsDendrometer());
          color = closestPair[1];
        }
      }
      return color;
    }
    const fillColor = shadeColor(getColor(sensorType, sensor), -30);
    const installationHeightValue = Number(installation?.height);
    const effectiveCrownHeight =
      Number.isFinite(installationHeightValue) && installationHeightValue > 0
        ? installationHeightValue
        : fallbackCrownHeight;
    const calculatedX =
      ["soil_moisture", "soil_water_tension"].includes(sensorType) &&
      soilHasBall &&
      installation.depth != 0
        ? calculateSensorXPosition(index + 3)
        : calculateSensorXPosition(index);

    const calculatedY = calculateSensorYPosition(
      ["soil_moisture", "soil_water_tension"].includes(sensorType)
        ? installation?.depth == 0
          ? -5
          : depthsSet.size > 1
          ? (30 - Number(installation?.depth || 0)) / 3
          : 0
        : treeHeightValue
        ? (effectiveCrownHeight / treeHeightValue) * 100
        : 70
    );
    const x = overridePosition?.x ?? calculatedX;
    const y = overridePosition?.y ?? calculatedY;
    const cursor = cursorOverride || (onDragStart ? "ns-resize" : "pointer");
    const handleMouseEnter =
      !disableHover && validLabelBrowser
        ? () => {
            setSensorInstallationHover(installation);
            requestAnimationFrame(() => {
              setSensorIconInfo({ x: x, y: y, name: installation.sensor.name });
            });
          }
        : undefined;
    const handleMouseLeave = disableHover
      ? undefined
      : () => {
          setSensorInstallationHover(null);
          requestAnimationFrame(() => {
            setSensorIconInfo(null);
          });
        };
    const Icon = (
      <svg
        x={x}
        y={y}
        viewBox="0 0 512 512"
        width="10%"
        height="10%"
        key={installation.id}
        onClick={onSensorClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseDown={
          onDragStart
            ? (event) => {
                event.preventDefault();
                event.stopPropagation();
                onDragStart(event);
              }
            : undefined
        }
        onTouchStart={
          onDragStart
            ? (event) => {
                event.preventDefault();
                event.stopPropagation();
                onDragStart(event);
              }
            : undefined
        }
        style={{
          cursor,
          fill: "none",
          stroke: getColor(sensorType, sensor),
        }}
      >
        {tooltip && <title>{tooltip}</title>}
        <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
        <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
        <g id="SVGRepo_iconCarrier">
          <circle cx="256" cy="256" r="240" fill="#EEEEEEEE" />{" "}
          <g>
            {" "}
            <g>
              {" "}
              <g>
                {" "}
                <path
                  clipRule="evenodd"
                  d="M415.698,192.641c-8.379-21.068-21.236-40.268-38.577-57.6 l36.309-36.364c26.971,26.989,45.555,57.456,55.745,91.404c6.226,20.683,9.349,42.655,9.367,65.919v0.06 c-0.008,34.011-6.66,65.254-19.957,93.729c-10.701,23.024-25.735,44.24-45.097,63.651l-36.427-36.422 c13.297-13.305,23.948-27.696,31.945-43.171c12.119-23.434,18.17-49.363,18.156-77.788V256 C427.19,233.287,423.372,212.168,415.698,192.641z"
                  fillRule="evenodd"
                  fill={fillColor}
                ></path>{" "}
              </g>{" "}
              <g>
                {" "}
                <path
                  clipRule="evenodd"
                  d="M351.69,160.99c8.768,8.825,16.062,18.27,21.88,28.334h0.06 c11.449,19.798,17.17,41.983,17.165,66.56V256c-0.004,25.516-6.152,48.478-18.447,68.889h-0.057 c-5.676,9.334-12.638,18.139-20.892,26.414v-0.06l-0.057,0.06l-36.427-36.423c2.371-2.448,4.562-4.95,6.576-7.505v-0.06 c11.828-14.672,17.763-31.779,17.807-51.315v-0.116c-0.019-22.938-8.124-42.506-24.323-58.705l36.483-36.538 C351.541,160.752,351.618,160.868,351.69,160.99z"
                  fill={fillColor}
                  fillRule="evenodd"
                ></path>{" "}
              </g>{" "}
              <g>
                {" "}
                <path
                  clipRule="evenodd"
                  d="M225.829,225.571c8.342-8.35,18.427-12.519,30.259-12.509 c11.851-0.01,21.956,4.159,30.316,12.509l0.059,0.059c8.313,8.348,12.463,18.433,12.451,30.254V256 c-0.016,11.798-4.187,21.863-12.51,30.197c-0.03,0.027-0.048,0.068-0.058,0.116c-8.364,8.272-18.447,12.402-30.259,12.394 c-11.817,0.009-21.884-4.142-30.201-12.452l-0.058-0.058c-8.311-8.317-12.481-18.365-12.511-30.138c0-0.06,0-0.117,0-0.176 c-0.012-11.787,4.121-21.854,12.395-30.196C225.747,225.653,225.786,225.615,225.829,225.571z"
                  fill={fillColor}
                  fillRule="evenodd"
                ></path>{" "}
              </g>{" "}
              <g>
                {" "}
                <path
                  clipRule="evenodd"
                  d="M225.829,225.571c8.342-8.35,18.427-12.519,30.259-12.509 c11.851-0.01,21.956,4.159,30.316,12.509l0.059,0.059c8.313,8.348,12.463,18.433,12.451,30.254V256 c-0.016,11.798-4.187,21.863-12.51,30.197c-0.03,0.027-0.048,0.068-0.058,0.116c-8.364,8.272-18.447,12.402-30.259,12.394 c-11.817,0.009-21.884-4.142-30.201-12.452l-0.058-0.058c-8.311-8.317-12.481-18.365-12.511-30.138c0-0.06,0-0.117,0-0.176 c-0.012-11.787,4.121-21.854,12.395-30.196C225.747,225.653,225.786,225.615,225.829,225.571z"
                  fill={fillColor}
                  fillRule="evenodd"
                ></path>{" "}
              </g>{" "}
              <g>
                {" "}
                <path
                  clipRule="evenodd"
                  d="M121.265,255.884c-0.006-1.646,0.014-3.273,0.059-4.887 c1.113-35.124,14.225-65.242,39.335-90.356l36.426,36.422c-16.217,16.235-24.306,35.842-24.265,58.821c0,0.059,0,0.116,0,0.176 c-0.015,2.561,0.083,5.084,0.291,7.563v0.057c1.61,19.688,9.602,36.736,23.974,51.143c0.035,0.037,0.073,0.075,0.117,0.116 l-36.485,36.423l-0.058-0.059c-26.237-26.289-39.368-58.037-39.394-95.243C121.265,256,121.265,255.942,121.265,255.884z"
                  fill={fillColor}
                  fillRule="evenodd"
                ></path>{" "}
              </g>{" "}
              <g>
                {" "}
                <path
                  clipRule="evenodd"
                  d="M35.437,225.28c6.121-48.44,27.186-90.661,63.192-126.662 l36.425,36.423c-29.26,29.239-45.727,63.78-49.402,103.621c-0.51,5.681-0.763,11.479-0.756,17.397 c0.002,47.206,16.684,87.524,50.042,120.959l0.058,0.059l-36.426,36.421l-0.058-0.058 c-43.359-43.459-65.025-95.92-64.997-157.381C33.521,245.532,34.161,235.271,35.437,225.28z"
                  fill={fillColor}
                  fillRule="evenodd"
                ></path>{" "}
              </g>{" "}
            </g>{" "}
          </g>{" "}
        </g>
      </svg>
    );
    return Icon;
  };
  const { mobile } = useSelector((state: any) => state.mobile);
  const getHealthStateCrown = (sensor) => {
    if ([2, 3, 4].includes(sensor.sensor_state)) {
      return 3;
    }
    const moistureContent = sensor?.pulse_sensor?.moisture_content;
    if (moistureContent > tree.blue_threshold) {
      return 5;
    }
    if (moistureContent > tree.green_threshold) {
      return 0;
    }
    if (moistureContent > tree.yellow_threshold) {
      return 1;
    }
    return 2;
  };
  const crownRef = useRef<SVGPathElement>(null);
  const soilRef = useRef<SVGRectElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasDepth120 = installations.some((installation) => installation.depth == 120);
  const showSoilPreviewLevels = ["soil_moisture", "soil_water_tension"].includes(previewSensorType);

  const [crownPos, setCrownPos] = useState({ top: 0, left: 0 });
  const [soilPos, setSoilPos] = useState({ top: 0, left: 0 });
  const [sensorInstallationHover, setSensorInstallationHover] = useState(null);
  const [sensorIconInfo, setSensorIconInfo] = useState(null);
  useEffect(() => {
    const updatePositions = () => {
      if (crownRef.current && soilRef.current && containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const crownBox = crownRef.current.getBoundingClientRect();
        const soilBox = soilRef.current.getBoundingClientRect();
        setCrownPos({
          top: crownBox.top - containerRect.top,
          left: crownBox.right - containerRect.left + 4,
        });

        setSoilPos({
          top: soilBox.top - containerRect.top + soilBox.height / 2 - 12,
          left: soilBox.right - containerRect.left + 4,
        });
      }
    };

    updatePositions();
    window.addEventListener("resize", updatePositions);
    return () => window.removeEventListener("resize", updatePositions);
  }, []);

  const renderSensorPanel = !mobile && selectedInstallation && showSelectedSensorPanel;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: mobile ? "column" : "row",
        alignItems: mobile ? "flex-start" : "center",
        justifyContent: renderSensorPanel ? "flex-start" : centerTree ? "center" : "flex-start",
        height: "auto",
        width: "100%",
      }}
    >
      {renderSensorPanel && (
        <div
          style={{
            height: "250px",
            width: "47%",
          }}
          className="leftOfTree"
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <h4>{selectedInstallation && selectedInstallation.sensor.name}</h4>
            {selectedInstallation && (
              <Box sx={{ display: "flex", alignSelf: "flex-end", justifyContent: "flex-end" }}>
                <Button
                  onClick={() => setShowData(true)}
                  style={{
                    border: "none",
                    fontSize: "1em",
                  }}
                >
                  <i className="bi bi-info-circle"></i>
                </Button>
              </Box>
            )}
          </div>
          {selectedInstallation &&
            (getHealthStateCrown(selectedInstallation.sensor) != 3 ||
              getSensorType(selectedInstallation.sensor) === "dendrometer") &&
            MoistureGauge({
              tree: tree,
              sensor: selectedInstallation.sensor,
              t: t,
              soilWaterTensionType: soilWaterTensionType,
              setSoilWaterTensionType: setSoilWaterTensionType,
              edit: edit,
              setEdit: setEdit,
              calibrationSpeciesName: calibrationSpeciesName,
            })}
        </div>
      )}
      <Box ref={containerRef} sx={{ position: "relative", width: "250px" }}>
        <svg
          ref={svgRef}
          width="250px"
          height={`${treeIconHeight}px`}
          viewBox={`0 0 256 ${treeIconHeight + 6}`}
          xmlns="http://www.w3.org/2000/svg"
          overflow="visible"
        >
          <g
            style={{
              stroke: "none",
              strokeWidth: 0,
              strokeDasharray: "none",
              strokeLinecap: "butt",
              strokeLinejoin: "miter",
              strokeMiterlimit: 10,
              fill: "none",
              fillRule: "nonzero",
              opacity: 1,
            }}
            transform="matrix(2.740322, 0, 0, 2.543772, 4.580997, 2.333565)"
          >
            {" "}
            <path
              id="crown1"
              ref={crownRef}
              // path
              d="M 51.865 1.523 c 2.639 0.961 4.659 2.757 6.26 5.115 c 5.117 -0.776 10.301 0.912 13.976 4.587 c 3.675 3.675 5.364 8.857 4.588 13.975 c 4.168 3.07 6.638 7.929 6.638 13.126 c 0 5.393 -2.644 10.387 -7.091 13.437 c -0.76 2.672 -2.184 5.104 -4.135 7.055 c -3.134 3.134 -7.323 4.754 -11.539 4.754 c -3.088 0 -6.19 -0.869 -8.902 -2.649 l 0.549 -0.836 l -10.447 -1.312 c 0 0 0 0 0 0 l -0.216 0.977 c -0.422 -0.094 -0.843 -0.204 -1.256 -0.329 c -3.807 3.412 -8.875 4.69 -13.641 3.882 c 0.009 -0.082 0.048 -0.316 0.054 -0.379 C 21.64 59.31 16.967 51.451 16.924 45 c -0.041 -6.216 2.696 -12.469 7.394 -16.172 c -0.92 -6.117 1.123 -11.997 4.951 -16.687 c 3.961 -4.852 10.438 -6.889 16.416 -5.562 C 45.933 3.689 48.897 2.544 51.865 1.523"
              style={{
                stroke: "none",
                strokeWidth: 1,
                strokeDasharray: "none",
                strokeLinecap: "butt",
                strokeLinejoin: "miter",
                strokeMiterlimit: 10,
                fill: fillCrownColor,
                fillRule: "nonzero",
                opacity: 1,
              }}
              transform=" matrix(1 0 0 1 0 0) "
              strokeLinecap="round"
            />
            <path
              id="crown2"
              // path
              d="M 26.702 62.925 C 21.64 59.31 17.809 51.551 17.767 45.1 c -0.041 -6.216 2.718 -12.047 7.415 -15.75 c -0.92 -6.117 0.952 -12.328 5.086 -16.751 c 4.133 -4.423 9.991 -6.482 15.793 -5.591 c 1.564 -2.275 3.552 -4.128 5.804 -5.483 C 49.745 0.537 47.41 0 44.999 0 c -5.197 0 -10.056 2.47 -13.126 6.639 c -5.117 -0.777 -10.3 0.912 -13.975 4.587 s -5.364 8.857 -4.587 13.975 c -4.169 3.07 -6.638 7.929 -6.638 13.126 c 0 5.393 2.644 10.387 7.091 13.437 c 0.759 2.67 2.183 5.103 4.135 7.055 c 2.456 2.456 5.534 3.942 8.749 4.487 C 26.656 63.223 26.696 62.987 26.702 62.925 z"
              style={{
                stroke: "none",
                strokeWidth: 1,
                strokeDasharray: "none",
                strokeLinecap: "butt",
                strokeLinejoin: "miter",
                strokeMiterlimit: 10,
                fill: fillCrown2Color,
                fillRule: "nonzero",
                opacity: 1,
              }}
              transform=" matrix(1 0 0 1 0 0) "
              strokeLinecap="round"
            />{" "}
            <path
              id="trunk"
              // path
              d="M 57.523 54.976 l -3.031 -2.947 c -0.387 -0.375 -0.999 -0.378 -1.388 -0.007 l -3.865 3.69 V 40.889 c 0 -0.552 -0.447 -1 -1 -1 h -4.275 c -0.552 0 -1 0.448 -1 1 v 7.186 l -4.745 -4.919 c -0.377 -0.391 -1.063 -0.391 -1.439 0 l -2.52 2.614 c -0.369 0.382 -0.374 0.986 -0.013 1.375 l 6.515 7.007 V 89 c 0 0.553 0.448 1 1 1 h 6.477 c 0.553 0 1 -0.447 1 -1 V 64.29 l 8.276 -7.872 c 0.197 -0.188 0.309 -0.447 0.311 -0.719 C 57.828 55.426 57.718 55.166 57.523 54.976 z"
              style={{
                stroke: "none",
                strokeWidth: 1,
                strokeDasharray: "none",
                strokeLinecap: "butt",
                strokeLinejoin: "miter",
                strokeMiterlimit: 10,
                fill: "rgb(160,126,99)",
                fillRule: "nonzero",
                opacity: 1,
              }}
              transform=" matrix(1 0 0 1 0 0) "
              strokeLinecap="round"
            />{" "}
          </g>{" "}
          <rect
            id="soil30"
            ref={soilRef}
            // rect
            x="12.128"
            y="230.382"
            width="230.817"
            height="25"
            style={{ fill: soilColor30 }}
            rx="2.961"
            ry="2.961"
          />
          {(depthsSet.size > 1 || showSoilPreviewLevels) && (
            <>
              <rect
                id="soil60"
                // rect
                x="12.128"
                y="255.382"
                width="230.817"
                height="25"
                style={{ fill: soilColor60 }}
                rx="2.961"
                ry="2.961"
              />
              <rect
                id="soil90"
                // rect
                x="12.128"
                y="280.382"
                width="230.817"
                height="25"
                style={{ fill: soilColor90 }}
                rx="2.961"
                ry="2.961"
              />
            </>
          )}
          {(depthsSet.size > 3 && hasDepth120) || showSoilPreviewLevels ? (
            <rect
              id="soil120"
              // rect
              x="12.128"
              y="305.382"
              width="230.817"
              height="25"
              style={{ fill: soilColor120 }}
              rx="2.961"
              ry="2.961"
            />
          ) : null}
          {(soilHasBall || showSoilPreviewLevels) && (
            <rect
              id="soilRootBall"
              // rect
              x="77.128"
              y="230.382"
              width="100.817"
              height="50"
              style={{ fill: soilColorBall }}
              rx="20"
              ry="20"
            />
          )}
          {previewInstallation && previewPosition && (
            <SensorIcon
              installation={previewInstallation}
              installations={installations}
              overridePosition={previewPosition}
              onSensorClick={onPreviewInstall ? () => onPreviewInstall() : undefined}
              onDragStart={previewDraggable ? handlePreviewStart : undefined}
              disableHover
              cursorOverride={
                previewDraggable ? "ns-resize" : onPreviewInstall ? "pointer" : "default"
              }
              tooltip={activePreviewTooltip}
            />
          )}
          {!hideInstallations &&
            installations.map(
              (installation) =>
                !installation.removed_at && (
                  <SensorIcon
                    onSensorClick={() => {
                      handleSensorClick(installation);
                    }}
                    key={installation.id}
                    installation={installation}
                    installations={installations}
                  />
                )
            )}
        </svg>
        {/* {validLabelBrowser && */}
        {sensorInstallationHover && sensorIconInfo && sensorLabel({ sensorIconInfo })}
        {/* Config / Threshold pencil */}
        {!hideEditButtons && (
          <>
            <IconButton
              className="tree-diagram-edit-btn"
              size="small"
              onClick={() => (onOpenConfigPanel ? onOpenConfigPanel("crown") : setEdit(true))}
              sx={{
                position: "absolute",
                top: `${crownPos.top}px`,
                left: `${crownPos.left}px`,
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              className="tree-diagram-edit-btn"
              size="small"
              onClick={() => (onOpenConfigPanel ? onOpenConfigPanel("soil") : setSoilEdit(true))}
              sx={{
                position: "absolute",
                top: `${soilPos.top}px`,
                left: `${soilPos.left}px`,
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </>
        )}
        {onAddSensor && (
          <Button
            size="sm"
            variant="primary"
            onClick={onAddSensor}
            style={{
              position: "absolute",
              left: "100%",
              bottom: "0",
              marginLeft: "12px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
              whiteSpace: "nowrap",
            }}
          >
            <AddIcon size="1em" className="me-1" />
            {t("install_sensor")}
          </Button>
        )}
        {mobile && selectedInstallation && (
          <div
            style={{
              width: "100%",
              padding: "10px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              <h4>{selectedInstallation && selectedInstallation.sensor.name}</h4>
              {selectedInstallation && (
                <Box sx={{ display: "flex", alignSelf: "flex-end", justifyContent: "flex-end" }}>
                  <Button
                    onClick={() => setShowData(true)}
                    style={{
                      border: "none",
                      fontSize: "1em",
                    }}
                  >
                    {/* {t("Manual")} */}
                    <i className="bi bi-info-circle"></i>
                    {/* <ManualIconWhite /> */}
                  </Button>
                </Box>
              )}
            </div>
            {selectedInstallation &&
              (getHealthStateCrown(selectedInstallation.sensor) != 3 ||
                getSensorType(selectedInstallation.sensor) === "dendrometer") &&
              MoistureGauge({
                tree: tree,
                sensor: selectedInstallation.sensor,
                t: t,
                soilWaterTensionType: soilWaterTensionType,
                setSoilWaterTensionType: setSoilWaterTensionType,
                edit: edit,
                setEdit: setEdit,
                calibrationSpeciesName: calibrationSpeciesName,
              })}
          </div>
        )}

        {/* Modal Component */}
        {selectedInstallation && showData && (
          <Modal
            onHide={() => {
              setShowData(false);
            }}
            show={!!selectedInstallation}
            centered
          >
            <ModalHeader closeButton>
              <Modal.Title>{selectedInstallation.sensor.name}</Modal.Title>
            </ModalHeader>
            <ModalBody>
              <p>
                <strong>{t("meaning_measurement")}</strong>:{" "}
                {treeStateDescription(t, selectedInstallation.sensor, tree)}
              </p>
              <p></p>
              <p>
                <strong>{t(`installed_at`)}</strong>:{" "}
                {selectedInstallation.installed_at
                  ? selectedInstallation.installed_at
                  : t("not_provided")}
              </p>
              <p>
                <strong>{t(`removed_at`)}</strong>:{" "}
                {selectedInstallation.removed_at
                  ? selectedInstallation.removed_at
                  : t("not_provided")}
              </p>
              <p>
                <strong>
                  {selectedInstallation.sensor.network_type == "NB" ? t(`imei`) : t(`dev_eui`)}
                </strong>
                :{" "}
                {selectedInstallation.sensor.network_type == "NB"
                  ? selectedInstallation.sensor.imei
                  : selectedInstallation.sensor.hardware_serial}
              </p>
            </ModalBody>
          </Modal>
        )}
        {!!soilEdit && (
          <Modal
            onHide={() => {
              setSoilEdit(false);
            }}
            show={!!soilEdit}
            centered
          >
            <ModalHeader closeButton>
              <Modal.Title>{t("adjust_thresholds_soil")}</Modal.Title>
            </ModalHeader>
            <ModalBody>
              <SoilThresholdSlider tree={tree} setEdit={setSoilEdit} />
            </ModalBody>
          </Modal>
        )}
        {!!edit && (
          <Modal
            onHide={() => {
              setEdit(false);
            }}
            show={!!edit}
            centered
          >
            <ModalHeader closeButton>
              <Modal.Title>{t("adjust_thresholds_crown")}</Modal.Title>
            </ModalHeader>
            <ModalBody>
              <HealthThresholdSlider tree={tree} setEdit={setEdit} />
            </ModalBody>
          </Modal>
        )}
      </Box>
    </div>
  );
};

export default Tree;
